import { useEffect, useState } from "react";
import {
  Globe, Bus, Path, Warning, ArrowSquareOut, CopySimple, CheckCircle,
  Database, Clock, ShieldCheck, Code,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEEDS = [
  {
    key: "vehicle_positions",
    label: "Vehicle Positions",
    desc: "Live GPS coordinates, speed, bearing & occupancy of every active KTCL bus.",
    path: "/api/open/gtfs-rt/vehicle-positions.pb",
    icon: Bus,
    color: "from-blue-500 to-indigo-600",
    refresh: "≤ 15s",
    age: "≤ 5 min",
  },
  {
    key: "trip_updates",
    label: "Trip Updates",
    desc: "Predicted arrival & departure times at every stop on each in-progress trip, with delay (sec).",
    path: "/api/open/gtfs-rt/trip-updates.pb",
    icon: Path,
    color: "from-amber-500 to-orange-600",
    refresh: "≤ 15s",
    age: "≤ 5 min",
  },
  {
    key: "service_alerts",
    label: "Service Alerts",
    desc: "Active breakdowns, GPS-offline incidents & disruptions affecting service.",
    path: "/api/open/gtfs-rt/service-alerts.pb",
    icon: Warning,
    color: "from-rose-500 to-red-600",
    refresh: "≤ 60s",
    age: "≤ 30 min",
  },
];

const STATIC_FEED = {
  label: "Static GTFS Feed (zip)",
  desc: "Routes, stops, trips, stop_times, calendar, fares — official KTCL schedule.",
  path: "/api/open/gtfs/feed.zip",
};

export default function OpenDataFeeds() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      for (const f of FEEDS) {
        try {
          const r = await fetch(`${BACKEND_URL}${f.path}?format=json`);
          const j = await r.json();
          next[f.key] = {
            entities: j?.entity?.length || 0,
            timestamp: Number(j?.header?.timestamp || 0),
          };
        } catch {
          next[f.key] = { entities: 0, timestamp: 0 };
        }
      }
      if (!cancelled) { setStats(next); setLoading(false); }
    })();
    const t = setInterval(() => window.location.reload(), 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="px-6 lg:px-8 py-6 max-w-7xl mx-auto" data-testid="open-data-feeds-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            <Globe size={14}/> Open Data
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 mt-0.5">GTFS-Realtime Feeds</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Public, no-auth Protocol Buffer feeds compliant with the official
            GTFS-Realtime v2.0 specification. Ingestible by Google Maps Transit
            Partners, Apple Maps, Moovit, Transit App, and OpenTripPlanner.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="https://gtfs.org/documentation/realtime/proto/" target="_blank" rel="noreferrer"
             data-testid="gtfs-spec-link"
             className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 px-3 py-2 border border-indigo-200 bg-indigo-50 rounded-lg flex items-center gap-1.5">
            GTFS-RT Spec <ArrowSquareOut size={12}/>
          </a>
          <a href={`${BACKEND_URL}/api/open/gtfs-rt`} target="_blank" rel="noreferrer"
             data-testid="discovery-doc-link"
             className="text-xs font-semibold text-slate-700 hover:text-slate-900 px-3 py-2 border border-slate-200 bg-white rounded-lg flex items-center gap-1.5">
            Discovery Doc <ArrowSquareOut size={12}/>
          </a>
        </div>
      </div>

      {/* Stat banner */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={ShieldCheck} label="Spec Version" value="v2.0" sub="GTFS-Realtime"/>
        <StatCard icon={Database} label="Static GTFS" value="476" sub="routes · 3,255 stops"/>
        <StatCard icon={Bus} label="Live GPS Buses" value={stats.vehicle_positions?.entities ?? "…"} sub="from KTCL PIS feed"/>
        <StatCard icon={Clock} label="Last Publish" value={stats.vehicle_positions?.timestamp ? formatAge(stats.vehicle_positions.timestamp) : "…"} sub="auto-refreshes every 30s"/>
      </div>

      {/* Realtime feed cards */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {FEEDS.map((f) => (
          <FeedCard key={f.key} feed={f} stat={stats[f.key]} loading={loading}/>
        ))}
      </div>

      {/* Static feed */}
      <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Database size={20} weight="bold"/>
          </div>
          <div className="flex-1">
            <div className="font-bold text-slate-900">{STATIC_FEED.label}</div>
            <div className="text-xs text-slate-600 mt-0.5">{STATIC_FEED.desc}</div>
            <UrlRow path={STATIC_FEED.path} label="Download zip"/>
          </div>
        </div>
      </div>

      {/* Code sample */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-950 p-5 text-slate-100">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">
          <Code size={14}/> Python sample · parse vehicle positions
        </div>
        <pre className="text-[12px] leading-relaxed font-mono overflow-x-auto" data-testid="code-sample">
{`import requests
from google.transit import gtfs_realtime_pb2

r = requests.get("${BACKEND_URL}/api/open/gtfs-rt/vehicle-positions.pb")
feed = gtfs_realtime_pb2.FeedMessage()
feed.ParseFromString(r.content)

for entity in feed.entity:
    v = entity.vehicle
    print(v.vehicle.id, v.position.latitude, v.position.longitude)`}
        </pre>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        <Icon size={12}/> {label}
      </div>
      <div className="text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function FeedCard({ feed, stat, loading }) {
  const url = `${BACKEND_URL}${feed.path}`;
  const Icon = feed.icon;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col" data-testid={`feed-card-${feed.key}`}>
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${feed.color} text-white flex items-center justify-center shadow-md shrink-0`}>
          <Icon size={20} weight="bold"/>
        </div>
        <div className="flex-1">
          <div className="font-bold text-slate-900 leading-tight">{feed.label}</div>
          <div className="text-[12px] text-slate-600 mt-1 leading-snug">{feed.desc}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 text-[11px] text-slate-500">
        <span className="px-2 py-0.5 rounded bg-slate-100 font-semibold">refresh {feed.refresh}</span>
        <span className="px-2 py-0.5 rounded bg-slate-100 font-semibold">data age {feed.age}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 text-center">
        <div className="bg-slate-50 rounded-lg py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Entities</div>
          <div className="text-base font-extrabold text-slate-900">{loading ? "…" : (stat?.entities ?? 0)}</div>
        </div>
        <div className="bg-slate-50 rounded-lg py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last build</div>
          <div className="text-base font-extrabold text-slate-900">
            {loading ? "…" : (stat?.timestamp ? formatAge(stat.timestamp) : "—")}
          </div>
        </div>
      </div>

      <UrlRow path={feed.path} label="Protobuf"/>
      <UrlRow path={`${feed.path}?format=json`} label="JSON debug" subtle/>
    </div>
  );
}

function UrlRow({ path, label, subtle }) {
  const url = `${BACKEND_URL}${path}`;
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(`${label} URL copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className={`mt-2 flex items-center gap-2 ${subtle ? "opacity-75" : ""}`}>
      <a href={url} target="_blank" rel="noreferrer"
         className="flex-1 truncate text-[11px] font-mono text-indigo-700 hover:text-indigo-900 bg-indigo-50/60 border border-indigo-200/60 rounded px-2 py-1.5"
         data-testid={`feed-url-${path.replace(/[^a-z0-9]+/gi,'-')}`}
         title={url}>
        {path}
      </a>
      <button onClick={onCopy} aria-label={`Copy ${label} URL`}
              data-testid={`copy-${path.replace(/[^a-z0-9]+/gi,'-')}`}
              className={`p-1.5 rounded border transition-colors ${
                copied
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}>
        {copied ? <CheckCircle size={14} weight="fill"/> : <CopySimple size={14}/>}
      </button>
    </div>
  );
}

function formatAge(ts) {
  if (!ts) return "—";
  const sec = Math.floor(Date.now() / 1000) - ts;
  if (sec < 0) return "live";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}
